import { getAccessToken } from '../authentication/supabaseService';

const API_BASE = '/api/admin';

export interface UserSettingPair {
  userId: string;
  settingKey: string;
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
  }

  return response.json();
}

export async function getAllSettings(): Promise<UserSettingPair[]> {
  return fetchWithAuth('/settings');
}

export async function getSettingsForUser(userId: string): Promise<string[]> {
  return fetchWithAuth(`/settings/${userId}`);
}

export async function addSetting(userId: string, settingKey: string): Promise<void> {
  return fetchWithAuth(`/settings/${userId}`, {
    method: 'POST',
    body: JSON.stringify({ settingKey }),
  });
}

export async function removeSetting(userId: string, settingKey: string): Promise<void> {
  return fetchWithAuth(`/settings/${userId}/${settingKey}`, {
    method: 'DELETE',
  });
}

/**
 * Get all user IDs that have a specific setting
 */
export async function getUserIdsWithSetting(settingKey: string): Promise<string[]> {
  const allSettings = await getAllSettings();
  return allSettings
    .filter(s => s.settingKey === settingKey)
    .map(s => s.userId);
}

/**
 * The demo accounts every super-admin overview keeps out (`excludeUserIds` query param).
 * A failure degrades to "exclude nothing" rather than breaking the page it feeds.
 */
export async function getDemoAccountUserIds(): Promise<string[]> {
  try {
    return await getUserIdsWithSetting('demo_account');
  } catch (e) {
    console.warn('Could not fetch demo account settings:', e);
    return [];
  }
}

// --- Pricing admin functions ---

export interface AdminPricing {
  setup_fee: number;
  price_per_restaurant: number;
  isCustom: boolean;
}

export async function getAdminPricing(userId: string): Promise<AdminPricing> {
  return fetchWithAuth(`/pricing/${userId}`);
}

export async function setAdminPricing(userId: string, pricing: { setup_fee: number; price_per_restaurant: number }): Promise<void> {
  return fetchWithAuth(`/pricing/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(pricing),
  });
}

export async function resetAdminPricing(userId: string): Promise<void> {
  return fetchWithAuth(`/pricing/${userId}`, {
    method: 'DELETE',
  });
}
